import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CLINIC } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

const LogoMark = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden="true">
        <path
            d="M12 3c-2.2 0-2.8 1-4.5 1S5 5.4 5 7.6c0 3.4 1.6 4.6 2.1 7.3.4 2 .6 6.1 2.2 6.1 1.7 0 1.2-4.5 2.7-4.5s1 4.5 2.7 4.5c1.6 0 1.8-4.1 2.2-6.1.5-2.7 2.1-3.9 2.1-7.3 0-2.2-.5-3.6-2.5-3.6S14.2 3 12 3Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
        />
    </svg>
);

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        scrollToId(href);
    };

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
                scrolled ? "border-b border-border bg-[#f8f6f0]/85 shadow-[0_8px_30px_rgba(11,31,42,0.06)] backdrop-blur-xl" : "bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-[4.25rem] max-w-7xl items-center justify-between px-5 lg:px-10">
                <button
                    data-testid="navbar-logo"
                    onClick={() => go("#home")}
                    className="flex items-center gap-2.5 text-ink"
                    aria-label="Smile Dental Clinic — home"
                >
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-ink text-teal-300">
                        <LogoMark />
                    </span>
                    <span className="font-display text-lg leading-tight">
                        Smile <em className="text-teal-accent">Dental</em> Clinic
                    </span>
                </button>

                <div className="hidden items-center gap-8 lg:flex">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => go(l.href)}
                            className="group relative text-sm font-semibold text-ink/70 transition-colors duration-300 hover:text-ink"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-teal-600 transition-[width] duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <button
                        data-testid="nav-book-appointment-btn"
                        onClick={() => go("#appointment")}
                        className="rounded-full bg-ink px-5 py-2.5 text-sm font-bold text-[#f8f6f0] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal-700"
                    >
                        Book Appointment
                    </button>
                </div>

                <button
                    data-testid="nav-mobile-menu-btn"
                    className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 text-ink lg:hidden"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-b border-border bg-[#f8f6f0]/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {NAV_LINKS.map((l) => (
                                <button
                                    key={l.href}
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => go(l.href)}
                                    className="rounded-xl px-4 py-3 text-left font-display text-xl text-ink transition-colors hover:bg-mist"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="mobile-book-appointment-btn"
                                onClick={() => go("#appointment")}
                                className="mt-2 rounded-full bg-ink px-5 py-3.5 text-sm font-bold text-[#f8f6f0]"
                            >
                                Book Appointment
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
